import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, message, Button, Alert } from 'antd';
import { ArrowLeftOutlined, LockOutlined } from '@ant-design/icons';
import Questionnaire from '@/components/Questionnaire';
import type { QuestionnaireSchema, QuestionnaireAnswers } from '@/types/questionnaire';
import { getQuestionnaireInfo, submitQuestionnaire } from '@/services/questionnaire';

const QuestionnairePage = () => {
  const navigate = useNavigate();
  const { questionnaireNumber } = useParams<{ questionnaireNumber: string }>();
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState<QuestionnaireSchema | null>(null);
  const [answers, setAnswers] = useState<QuestionnaireAnswers | undefined>(undefined);
  const [readOnly, setReadOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 加载问卷信息
  useEffect(() => {
    if (!questionnaireNumber) {
      message.error('问卷编号不能为空');
      navigate('/');
      return;
    }

    const loadQuestionnaire = async () => {
      try {
        setLoading(true);
        const num = parseInt(questionnaireNumber, 10);
        if (isNaN(num)) {
          message.error('无效的问卷编号');
          navigate('/');
          return;
        }

        // 获取问卷信息（包含 Schema、答案和 readOnly 状态）
        const questionnaireInfo = await getQuestionnaireInfo(num);

        setSchema(questionnaireInfo.schema);
        if (questionnaireInfo.answers) {
          setAnswers(questionnaireInfo.answers);
        }
        // 设置只读状态（如果用户已完成问卷，后端会返回 readOnly: true）
        setReadOnly(questionnaireInfo.readOnly ?? false);
      } catch (error) {
        message.error('加载问卷失败，请稍后重试');
        // eslint-disable-next-line no-console
        console.error('加载问卷失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
  }, [questionnaireNumber, navigate]);

  // 处理提交
  const handleSubmit = async (submittedAnswers: QuestionnaireAnswers) => {
    if (readOnly) {
      message.warning('该问卷已完成，无法再次提交');
      return;
    }

    if (!questionnaireNumber) {
      message.error('问卷编号不能为空');
      return;
    }

    try {
      setSubmitting(true);
      const num = parseInt(questionnaireNumber, 10);
      const result = await submitQuestionnaire(num, submittedAnswers);

      if (result.success) {
        // 提交成功后，设置为只读状态
        setReadOnly(true);
        navigate(`/profile`);
      } else {
        message.error(result.message || '提交失败，请稍后重试');
      }
    } catch (error) {
      message.error('提交失败，请稍后重试');
      // eslint-disable-next-line no-console
      console.error('提交问卷失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 处理返回
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
        <Spin size="large" tip="加载问卷中..." />
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] space-y-4">
        <p className="text-slate-600">问卷加载失败，请稍后重试</p>
        <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
          返回
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* 只读提示 */}
      {readOnly && (
        <Alert
          message="问卷已完成"
          description="该问卷已完成填写，当前为只读模式，无法修改。"
          type="info"
          icon={<LockOutlined />}
          showIcon
          className="mb-4"
        />
      )}

      <Spin spinning={submitting}>
        {/* 问卷组件 */}
        <Questionnaire
          schema={schema}
          answers={answers}
          readOnly={readOnly}
          cancelLabel="返回"
          onSubmit={handleSubmit}
          onCancel={handleBack}
          onChange={(newAnswers) => {
            // 只读模式下不允许修改
            if (!readOnly) {
              setAnswers(newAnswers);
            }
          }}
        />
      </Spin>
    </div>
  );
};

export default QuestionnairePage;
